import { useSelector } from 'react-redux'
import { useCallback } from 'react'
import { actionGoalOptionsSelector } from '@src/selectors/app'
import { ActionGoalSummary } from '@src/types'
import { currentWeekSummariesSelector } from './models/actionGoals.selectors'
import { getDisplayUnitsChunk } from './utils'

export const useGetActionGoalOptionFromSummary = () => {
  const actionGoalOptions = useSelector(actionGoalOptionsSelector)

  const getActionGoalOptionFromSummary = useCallback(
    (summary: ActionGoalSummary) => {
      return actionGoalOptions.find((opt) => opt.kind === summary.actionGoal.kind)
    },
    [actionGoalOptions],
  )

  return getActionGoalOptionFromSummary
}

export const useGetShortDescriptionFromSummary = () => {
  const getMetricOptionFromSummary = useGetMetricOptionFromSummary()
  return useCallback(
    (summary: ActionGoalSummary) => {
      const metricOption = getMetricOptionFromSummary(summary)
      if (!metricOption) {
        return ''
      }
      return `${summary.actionGoal.title}: ${
        metricOption.valuePrefix ? metricOption.valuePrefix + ' ' : ''
      }${summary.actionGoal.metricValue}${getDisplayUnitsChunk(summary.actionGoal.displayUnits)}`
    },
    [getMetricOptionFromSummary],
  )
}

export const useGetMetricOptionFromSummary = () => {
  const actionGoalOptions = useSelector(actionGoalOptionsSelector)

  const getMetricOptionFromSummary = useCallback(
    (summary: ActionGoalSummary) => {
      const actionGoalOption = actionGoalOptions.find((opt) => opt.kind === summary.actionGoal.kind)
      if (!actionGoalOption) {
        return undefined
      }
      return actionGoalOption.metricOptions.find(
        (metricOpt) => metricOpt.kind === summary.actionGoal.metricKind,
      )
    },
    [actionGoalOptions],
  )

  return getMetricOptionFromSummary
}

type WeeklyProgress = {
  numberDaysTotal: number
  numberDaysCompleted: number
}

export const useGetActionGoalWeeklyProgress = (summaries?: ActionGoalSummary[]) => {
  const actionGoalSummaries = useSelector(currentWeekSummariesSelector)
  const workingSummaries = summaries || actionGoalSummaries

  const weeklyProgress: WeeklyProgress = { numberDaysTotal: 0, numberDaysCompleted: 0 }

  return workingSummaries.reduce((acc, summary) => {
    const { completions } = summary
    acc.numberDaysTotal += summary.actionGoal.numberDays

    if (!completions || completions.length === 0) {
      return acc
    }
    acc.numberDaysCompleted += completions.length
    return acc
  }, weeklyProgress)
}
